import { ConfigConsumer } from 'antd/lib/config-provider'
import LocaleReceiver from 'antd/lib/locale-provider/LocaleReceiver'
import type { TableProps as AntdTableProps } from 'antd/lib/table'
import { default as AntdTable } from 'antd/lib/table'
import Column from 'antd/lib/table/Column'
import ColumnGroup from 'antd/lib/table/ColumnGroup'
import classNames from 'classnames'
import React, { useMemo, useRef } from 'react'

import { TableContext } from './context'
import { useColumnSetting } from './hooks'
import { normalizeColumns } from './utils'

export type TableProps<T> = Omit<AntdTableProps<T>, 'locale'> & {
  storageKey?: string
  enableToolbar?: boolean
  showCellBorders?: boolean
  striped?: boolean
  shadowed?: boolean
  locale?: AntdTableProps<T>['locale'] & {
    loadingDescription?: string
    emptyText?: string
  }
  refresh?(): void
}

export const Table = <T,>({
  className,
  locale,
  bordered,
  size,
  storageKey,
  columns,
  children,
  shadowed = !bordered,
  striped = false,
  showCellBorders = false,
  pagination = {
    showTotal: (total) => `共 ${total} 条记录`,
  },
  enableToolbar = false,
  refresh,
  ...rest
}: TableProps<T>) => {
  const tableContainer = useRef<HTMLDivElement>(null)
  // const [innerSize, setInnerSize] = useLocalStorage<TableProps<T>['size']>(
  //   TABLE_SIZE_STORAGE_KEY,
  //   'default'
  // )
  const normalizedColumns = useMemo(
    () => columns || normalizeColumns<T>(children as React.ReactChildren),
    [children, columns]
  )
  const [displayColumns, columnSetting, setColumnSetting] = useColumnSetting(
    normalizedColumns,
    storageKey
  )

  /**
   * update the innerSize when props size changes
   */
  // useEffect(() => {
  //   if (size) {
  //     setInnerSize(size)
  //   }
  // }, [setInnerSize, size])

  return (
    <TableContext.Provider
      value={{
        // size: innerSize,
        tableContainer,
        columnSetting,
        refresh,
        // setSize: setInnerSize,
        setColumnSetting,
      }}
    >
      <ConfigConsumer>
        {({ getPrefixCls }) => {
          const prefixCls = getPrefixCls('table')

          return (
            <div ref={tableContainer}>
              <LocaleReceiver componentName="Table" defaultLocale={locale}>
                {(localeObject) => {
                  const locale = localeObject as Record<string, string>

                  return (
                    <AntdTable
                      className={classNames(
                        prefixCls,
                        {
                          [`${prefixCls}-borderless`]: !bordered,
                          [`${prefixCls}-hide-cell-borders`]: !showCellBorders,
                          [`${prefixCls}-size-small`]: size === 'small',
                          [`${prefixCls}-size-middle`]: size === 'middle',
                          [`${prefixCls}-size-middle`]: size === 'middle',
                          [`${prefixCls}-striped`]: striped,
                          [`${prefixCls}-shadowed`]: shadowed,
                        },
                        className
                      )}
                      locale={locale}
                      bordered={bordered}
                      size={size}
                      columns={displayColumns}
                      pagination={pagination}
                      {...rest}
                    />
                  )
                }}
              </LocaleReceiver>
            </div>
          )
        }}
      </ConfigConsumer>
    </TableContext.Provider>
  )
}

Table.Column = Column
Table.ColumnGroup = ColumnGroup
Table.displayName = 'Table'
